import {Box, Typography as MuiTypo, styled, useTheme} from '@mui/material';
import Avatar from '@jobseeker/lotus/components/Avatar';
import LinearProgressWithLabel from '@jobseeker/lotus/elements/ProgressBar';
import LinkageBanner from '@jobseeker/lotus/components/LinkageBanner';
import {getBarColor, getOldLink} from '@jobseeker/lotus/utils';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {useEffect, useState} from 'react';
import {handleTracking} from '@jobseeker/common-core/utils/analytics';
import type {
  dropDownLink,
  UserProfileCardProps,
} from '@jobseeker/lotus/components/UserProfileCard/type';
import {deleteCookie} from 'cookies-next';

import {FaChevronDown} from 'react-icons/fa';
import SpotLightBadge from '@jobseeker/lotus/components/SpotLightBadge';
import {useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';

const Typography = styled(MuiTypo)<any>((props: any) => ({
  lineHeight: 'normal',
  fontWeight: props.bold && 600,
  margin: '0 0 5px',
}));

const PLAN_INFO = [
  {
    title: 'Looking for a Perfect Resume',
    description: 'Create one with hirist Resume Builder',
    link: '/subscribe/plans?scrollTo=resumeBuilder',
    planCode: 'hirist-RB',
    display: 'none',
  },
  {
    title: 'Be an early applicant on jobs',
    description: 'Get notified as soon as a relevant job is posted',
    link: '/subscribe/plans?scrollTo=earlyApplicant',
    planCode: 'hirist-EA',
    display: 'none',
  },
];

const getDigits = (digit: number) =>
  digit ? (digit > 1000 ? `${digit / 1000}k` : digit) : 0;

const ProfileCard = ({
  profileImgUrl,
  name,
  email,
  profileProgress,
  showSpotLight,
  subscription,
  savedJobs,
  interviews,
  appliedJobs,
  dropDownLinks,
  enableFull,
  setExpandProfile,
}: Partial<UserProfileCardProps>) => {
  const {profileStatus} = useSelector(
    (state: AppState) => state.user.completeness
  );
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const theme = useTheme();
  const [subscriptionBanner, setSubscriptionBanner] = useState([...PLAN_INFO]);
  const [onboardingProgress, setOnboardingProgress] = useState<dropDownLink>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [initialFlow, setFlow] = useState(0);
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);

  const handleLinkageClick = (planCode: string) => {
    const event =
      planCode == 'hirist-EA'
        ? 'jsClickEA_ProfileStatus'
        : 'jsClickRB_ProfileStatus';

    if (window && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const ref = urlParams.get('ref') ?? '';

      handleTracking(event, {
        event_category: 'Subscription',
        Origin: 'Jobfeed',
        RefText: ref,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
      });

      const link = `/subscribe/plans?ref=${ref}&source=Jobfeed&origin=profileStatus&scrollTo=${
        planCode == 'hirist-EA' ? 'earlyApplicant' : 'resumeBuilder'
      }`;

      openUrl(link, '_self');
    }
  };

  const changeBanner = (slides = false) => {
    const planMapping: {[key: string]: boolean} = {
      'hirist-EA': !subscription?.earlyApplicant,
      'hirist-RB': !subscription?.resumeBuilder,
    };

    const filteredSubscriptionBanner = subscriptionBanner.filter(
      banner => planMapping[banner.planCode] ?? true
    );

    if (filteredSubscriptionBanner.length === 0) return; // Prevent errors when all banners are filtered

    // Reset all banners to 'none'
    const updatedBanners = filteredSubscriptionBanner.map(banner => ({
      ...banner,
      display: 'none',
    }));

    // Determine next slide index
    const nextSlide =
      currentSlide >= filteredSubscriptionBanner.length ? 0 : currentSlide;
    updatedBanners[nextSlide].display = 'block'; // Show only the next banner

    // Update state with a delay if slides are enabled
    const updateState = () => {
      setCurrentSlide(nextSlide + 1);
      setSubscriptionBanner([...updatedBanners]);
    };

    slides ? setTimeout(updateState, 5000) : updateState();

    !slides && setFlow(1);
  };

  const handleSideBar = () => {
    setTimeout(() => {
      setExpandProfile?.(prev => !prev);
      setShowProfileDrawer(prev => !prev);
    }, 0);
  };

  useEffect(() => {
    if (dropDownLinks) {
      setOnboardingProgress(
        dropDownLinks?.filter(item => !item.isCompleted)[0]
      );
    }
    changeBanner();
  }, []);

  useEffect(() => {
    if (initialFlow == 0) return;
    changeBanner(true);
  }, [currentSlide]);

  return (
    <Box sx={{width: '100%'}}>
      <Box sx={{display: 'flex', gap: theme.spacing(2)}}>
        <Box display={'inline-grid'} textAlign={'center'}>
          <Avatar
            imgUrl={profileImgUrl}
            isOnline
            sx={{width: {xxs: 40, md: 48}, height: {xxs: 40, md: 48}}}
          />
          <MuiTypo
            component="a"
            href={'/registration/addPersonalDetails'}
            target="_blank"
            color="#0057ff"
            fontSize={10}
            onClick={e => {
              localStorage.removeItem('filter');
              deleteCookie('filter');
            }}
            sx={{
              textDecoration: 'none',
              mt: showSpotLight ? -5.25 : isAppIimjobs ? 1 : -1,
            }}
          >
            EDIT
          </MuiTypo>
        </Box>
        <Box width={'70%'}>
          <Typography
            bold
            component={'p'}
            variant="subtitle1"
            color="secondary.main"
          >
            {name}
          </Typography>
          <Typography
            component={'p'}
            fontSize={14}
            fontWeight={400}
            color="secondary.main"
          >
            {email}
          </Typography>
          <LinearProgressWithLabel
            value={profileProgress ?? 0}
            barColor={getBarColor(profileProgress ?? 0)}
            hideTitle={true}
            sx={{
              mt: 1,
            }}
          />
          {onboardingProgress?.editText && (
            <MuiTypo
              component="a"
              href={onboardingProgress?.url}
              target="_blank"
              color="#0057ff"
              fontSize={10}
              sx={{
                textDecoration: 'none',
                textTransform: 'uppercase',
                mt: 0.65,
                ':hover': {
                  textDecoration: 'none',
                  color: '#212529',
                },
              }}
            >
              {`+ ${onboardingProgress?.editText}`}
            </MuiTypo>
          )}
          {!!showSpotLight && <SpotLightBadge />}
        </Box>
        {setExpandProfile && (
          <Box onClick={handleSideBar} pt={2}>
            <FaChevronDown
              cursor="pointer"
              style={{
                transform: showProfileDrawer
                  ? 'rotate(90deg)'
                  : 'rotate(270deg)',
              }}
              width={12}
              height={12}
              fontSize={12}
            />
          </Box>
        )}
      </Box>

      {subscription?.earlyApplicant && subscription?.resumeBuilder ? (
        <Box
          display={'flex'}
          sx={{
            backgroundColor: '#f2f5fa',
            p: 0.625,
            marginTop: 2.25,
            borderRadius: 2,
            mr: enableFull ? 0 : -2,
            justifyContent: 'space-around',
          }}
        >
          {[
            {
              title: 'Applied Jobs',
              value: getDigits(appliedJobs ?? 0),
              link: '/applied-jobs?ref=menu',
              enabled: !!appliedJobs,
            },
            {
              title: 'Saved Jobs',
              value: getDigits(savedJobs ?? 0),
              link: '/saved-jobs?ref=menu',
              enabled: !!savedJobs,
            },
            {
              title: 'My Interviews',
              value: getDigits(interviews ?? 0),
              link: '/interviews?ref=menu',
              enabled: !!interviews,
            },
          ].map((item, index) => (
            <>
              {item.enabled && (
                <Box
                  key={`${index}_profile_stats`}
                  component={'a'}
                  href={item.link}
                  target="_blank"
                  border={'none'}
                  sx={{
                    borderRadius: 2,
                    textDecoration: 'none',
                    transition: 'all .3s',
                    textAlign: 'center',
                    height: '73px',
                    width: '33%',
                    ':hover': {
                      backgroundColor: '#FFFFFF',
                      border: 'none',
                    },
                  }}
                >
                  <MuiTypo
                    component={'p'}
                    fontWeight={700}
                    color={'text.secondary'}
                    fontSize={24}
                    margin={'5px 0'}
                    lineHeight={'normal'}
                  >
                    {item.value}
                  </MuiTypo>
                  <MuiTypo
                    component={'h2'}
                    fontSize={11}
                    fontWeight={400}
                    color={'text.secondary'}
                    lineHeight={'normal'}
                    sx={{
                      opacity: 0.5,
                      textTransform: 'uppercase',
                      margin: '0 0 7px',
                      wordSpacing: '9999999px',
                    }}
                  >
                    {item.title}
                  </MuiTypo>
                </Box>
              )}
            </>
          ))}
        </Box>
      ) : (
        subscriptionBanner.map((info, index) => (
          <LinkageBanner
            key={`linkage_banner_${index}`}
            bannerInfo={{
              title: info.title,
              description: info.description,
              display: info.display,
            }}
            trackerId={info.planCode}
            circularMappers={subscriptionBanner.length}
            parentIndex={index}
            action={{
              text: 'SEE HOW',
              handler: () => {
                handleLinkageClick(info.planCode);
              },
            }}
            enableFull={enableFull}
          />
        ))
      )}
    </Box>
  );
};

export default ProfileCard;
